import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';
import type { Profile, CreateProfileRequest } from '../types/api';

const ProfilesPage: React.FC = () => {
  const { user, hasScope } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<CreateProfileRequest>({
    userId: user?._id || user?.id || '',
    accno: '',
    accname: '',
    accbank: '',
    callbackurl: '',
    currency: 'myr'
  });

  const isDeveloper = hasScope('dev');


  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      setError('');

      console.log('🔄 Loading profiles...', { isDeveloper, user: user?.id });

      const response = isDeveloper
        ? await profileService.getAllProfiles()
        : await profileService.getUserProfiles();

      console.log('📋 Profiles response:', response);

      if (response.success && response.data) {
        // Handle both array response and paginated response
        const profilesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setProfiles(profilesData);
        console.log('✅ Profiles loaded:', profilesData.length);
      } else {
        const errorMsg = response.message || 'Failed to load profiles';
        setError(errorMsg);
        console.error('❌ Failed to load profiles:', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to load profiles';
      setError(errorMsg);
      console.error('❌ Error loading profiles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProfile = async (profileData: CreateProfileRequest) => {
    try {
      setError('');

      // Validate the data
      const validationErrors = profileService.validateProfileData(profileData);
      if (validationErrors.length > 0) {
        setError(validationErrors.join(', '));
        return;
      }

      const response = await profileService.createProfile(profileData);

      if (response.success && response.data) {
        setProfiles([...profiles, response.data]);
        setShowCreateForm(false);
        resetForm();
        // Reload profiles to get updated data
        loadProfiles();
      } else {
        setError(response.message || 'Failed to create profile');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create profile');
    }
  };

  const resetForm = () => {
    setFormData({
      userId: user?._id || user?.id || '',
      accno: '',
      accname: '',
      accbank: '',
      callbackurl: '',
      currency: 'myr'
    });
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;

    try {
      setError('');
      const response = await profileService.deleteProfile(profileId);

      if (response.success) {
        setProfiles(profiles.filter(p => p._id !== profileId));
      } else {
        setError(response.message || 'Failed to delete profile');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to delete profile');
    }
  };

  const handleToggleStatus = async (profileId: string) => {
    try {
      setError('');
      const response = await profileService.toggleProfileStatus(profileId);

      if (response.success && response.data) {
        setProfiles(profiles.map(p =>
          p._id === profileId ? response.data! : p
        ));
      } else {
        setError(response.message || 'Failed to update profile status');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update profile status');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payment Profiles</h1>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">
                Manage your payment profiles and API credentials
              </p>
            </div>
            <button
              onClick={
                () => {
                  alert("Access denied")
                  // setShowCreateForm(true)
                }
              }
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base self-start sm:self-auto"
            >
              Create Profile
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Profiles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {profiles.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-400 text-lg">No profiles found</div>
                <p className="text-gray-500 mt-2">Create your first payment profile to get started</p>
              </div>
            ) : (
              profiles.map((profile) => (
                <div key={profile._id} className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{profile.accname}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ml-2 ${
                        profile.status
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {profile.status ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="space-y-2 text-xs sm:text-sm text-gray-600">
                      <div><strong>Bank:</strong> {profileService.getBankDisplayName(profile.accbank)}</div>
                      <div><strong>Account:</strong> {profile.accno}</div>
                      <div><strong>Balance:</strong> {profileService.formatCurrency(profile.balance, profile.currency)}</div>
                      <div><strong>API Key:</strong> {profile.apikey.substring(0, 8)}...</div>
                    </div>

                    <div className="mt-4 hidden flex flex-wrap gap-2">
                      <button
                        onClick={() => handleToggleStatus(profile._id)}
                        className={`text-xs sm:text-sm font-medium ${
                          profile.status
                            ? 'text-orange-600 hover:text-orange-800'
                            : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {profile.status ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium">
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProfile(profile._id)}
                        className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Profile Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Create New Profile</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleCreateProfile(formData);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Name</label>
                    <input
                      type="text"
                      required
                      value={formData.accname}
                      onChange={(e) => setFormData({...formData, accname: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Enter account name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bank</label>
                    <select
                      value={formData.accbank}
                      onChange={(e) => setFormData({...formData, accbank: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Bank</option>
                      {profileService.getAvailableBanks().map(bank => (
                        <option key={bank.code} value={bank.code}>{bank.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Number</label>
                    <input
                      type="text"
                      required
                      value={formData.accno}
                      onChange={(e) => setFormData({...formData, accno: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Enter account number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({...formData, currency: e.target.value as any})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      {profileService.getAvailableCurrencies().map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.name} ({currency.symbol})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Callback URL (Optional)</label>
                    <input
                      type="url"
                      value={formData.callbackurl}
                      onChange={(e) => setFormData({...formData, callbackurl: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="https://your-website.com/callback"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilesPage;
